import { useEffect } from 'react';
import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { currentType } from '@/lib/environment';
import { useWorkspaceStore } from '@/store/workspace';

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'free';
export type SubscriptionTier = 'free' | 'pro';

export interface Subscription {
  id: string;
  workspace_id: string;
  type: string;
  status: SubscriptionStatus;
  tier: SubscriptionTier;
  trial_start_date: string | null;
  trial_end_date: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
}

interface SubscriptionState {
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  // Tracks which workspace's subscription is currently mirrored, so the
  // hook below can detect a stale fetch when the user switches workspaces
  // mid-flight.
  loadedWorkspaceId: string | null;

  // Actions
  fetchSubscription: () => Promise<void>;
  createCheckoutSession: (priceId: string) => Promise<string | null>;
  createPortalSession: () => Promise<string | null>;
  clearSubscription: () => void;

  // Helpers
  isTrialing: () => boolean;
  isPro: () => boolean;
  isFree: () => boolean;
  canUseAI: () => boolean;
  getProjectLimit: () => number;
  getTrialDaysRemaining: () => number;
  hasActiveSubscription: () => boolean;
}

// Project limit for free tier
const FREE_PROJECT_LIMIT = 3;
const PRO_PROJECT_LIMIT = Infinity;

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  subscription: null,
  isLoading: false,
  error: null,
  loadedWorkspaceId: null,

  fetchSubscription: async () => {
    const workspace = useWorkspaceStore.getState().currentWorkspace;
    if (!workspace) {
      set({ subscription: null, isLoading: false, loadedWorkspaceId: null });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('workspace_id', workspace.id)
        .single();

      // If the user switched workspaces mid-fetch, this result is stale —
      // drop it.
      const stillCurrent = useWorkspaceStore.getState().currentWorkspace?.id;
      if (stillCurrent !== workspace.id) return;

      if (error) {
        // No subscription found — workspace is on implicit free tier
        // (e.g., subscription insert failed during createWorkspace and the
        // compensating delete also failed leaving an orphan workspace).
        if (error.code === 'PGRST116') {
          set({ subscription: null, isLoading: false, loadedWorkspaceId: workspace.id });
          return;
        }
        throw error;
      }

      set({
        subscription: data as Subscription,
        isLoading: false,
        loadedWorkspaceId: workspace.id,
      });
    } catch (error) {
      console.error('Error fetching subscription:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch subscription',
        isLoading: false,
      });
    }
  },

  createCheckoutSession: async (priceId: string) => {
    try {
      const workspace = useWorkspaceStore.getState().currentWorkspace;
      if (!workspace) {
        throw new Error('No workspace selected');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated - no session');
      }

      const response = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
          successUrl: `${window.location.origin}/checkout-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/settings`,
          workspace_id: workspace.id,
          type: currentType(),
        },
      });

      const { data, error } = response;

      if (error) {
        console.error('Checkout session error:', error);
        throw new Error(error.message || 'Checkout failed');
      }

      if (!data?.url) {
        throw new Error('No checkout URL returned');
      }

      return data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create checkout session' });
      return null;
    }
  },

  createPortalSession: async () => {
    try {
      const workspace = useWorkspaceStore.getState().currentWorkspace;
      if (!workspace) {
        throw new Error('No workspace selected');
      }

      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {
          returnUrl: `${window.location.origin}/settings`,
          workspace_id: workspace.id,
          type: currentType(),
        },
      });

      if (error) throw error;
      return data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create portal session' });
      return null;
    }
  },

  clearSubscription: () => {
    set({ subscription: null, error: null, loadedWorkspaceId: null });
  },

  isTrialing: () => {
    const { subscription } = get();
    return subscription?.status === 'trialing';
  },

  isPro: () => {
    const { subscription } = get();
    if (!subscription) return false;
    return subscription.tier === 'pro' &&
           (subscription.status === 'trialing' || subscription.status === 'active');
  },

  isFree: () => {
    const { subscription } = get();
    if (!subscription) return true; // No subscription = free tier
    return subscription.tier === 'free' || subscription.status === 'free';
  },

  canUseAI: () => {
    return get().isPro();
  },

  getProjectLimit: () => {
    return get().isPro() ? PRO_PROJECT_LIMIT : FREE_PROJECT_LIMIT;
  },

  getTrialDaysRemaining: () => {
    const { subscription } = get();
    if (!subscription?.trial_end_date) return 0;
    if (subscription.status !== 'trialing') return 0;

    const trialEnd = new Date(subscription.trial_end_date);
    const now = new Date();
    const diffMs = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  },

  hasActiveSubscription: () => {
    const { subscription } = get();
    if (!subscription) return false;
    return subscription.status === 'trialing' ||
           subscription.status === 'active';
  },
}));

// Helper hook for components that just need subscription helpers. The
// useEffect below auto-refetches whenever the current workspace changes
// so callers don't have to wire that themselves.
export const useSubscription = () => {
  const store = useSubscriptionStore();
  const currentWorkspaceId = useWorkspaceStore(s => s.currentWorkspace?.id ?? null);

  useEffect(() => {
    store.fetchSubscription();
    // Intentionally only re-run when the workspace identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]);

  return {
    subscription: store.subscription,
    isLoading: store.isLoading,
    isTrialing: store.isTrialing(),
    isPro: store.isPro(),
    isFree: store.isFree(),
    canUseAI: store.canUseAI(),
    projectLimit: store.getProjectLimit(),
    trialDaysRemaining: store.getTrialDaysRemaining(),
    hasActiveSubscription: store.hasActiveSubscription(),
    fetchSubscription: store.fetchSubscription,
    createPortalSession: store.createPortalSession,
    createCheckoutSession: store.createCheckoutSession,
  };
};
